// Pre-rendered first screen: hydration boot.
//
// The promoted file (build/prerendered/en/<config>.html → index.html) ships the
// REAL first-screen markup inside #root — captured locally from a live render by
// commands/prerenderSnapshot.js and re-hashed for the current build by
// commands/prerenderFirstScreen.js — plus <script id="__INITIAL_CONFIG__">.
//
// On load we:
//   1. verify the URL variant matches the baked one — the promoted file is served
//      to EVERY visitor, so a different ?config gets the classic SPA boot instead
//      (static markup wiped → React renders the RIGHT screen from scratch);
//   2. pre-set the survey stores (setupSurvey) so React's first render sees the
//      engine exactly as the captured markup did;
//   3. resolve the first screen's lazy chunk (preloadStepComponent) so nothing
//      suspends during hydration — a suspending hydration would drop the static
//      DOM instead of adopting it;
//   4. hydrateRoot — React adopts the static DOM in place; the carousel, the 5s
//      loader, welcome_loader analytics and Continue all run natively in the
//      component. No vanilla controllers, no boot-on-click.
//
// While main.js loads, the static screen shows the frozen initial frame (loader
// at 0%); the Continue button only appears ~5s AFTER hydration starts the loader,
// so user clicks always land on live React.
//
// A normal index.html has no __INITIAL_CONFIG__ → SPA boots exactly as before.

import setupSurvey from '../../survey/setupSurvey';
import { preloadStepComponent } from '../../survey/stepsComponents';
import { useAppStore, useQuestionsStore } from '../../store';

type WithInitialState = {
  getInitialState: () => Record<string, unknown>;
  getState: () => Record<string, unknown>;
};

// zustand v5 gives React's useSyncExternalStore a SERVER snapshot from the store's
// internal getInitialState() during hydration — the state captured at store
// CREATION, not the survey state we pre-set. The internal api object is not
// exported (create() only copies its methods onto the hook), but getInitialState()
// returns a stable object reference — so copying the live state onto it makes the
// hydration render see exactly what the captured markup was rendered with.
function syncServerSnapshot(store: WithInitialState): void {
  Object.assign(store.getInitialState(), store.getState());
}

// LCP guard: by the time the first-screen chunk resolves, the hero image is
// usually downloaded but not yet painted — and hydrateRoot then occupies the
// main thread for seconds, so the LCP frame would slide to hydration commit.
// Let the browser decode the hero and commit one frame before we block.
// Never rejects and never waits longer than HERO_PAINT_TIMEOUT_MS; in a hidden
// tab rAF does not tick, so we resolve immediately instead.
const HERO_PAINT_TIMEOUT_MS = 3000;

function waitHeroPaint(rootElement: HTMLElement): Promise<void> {
  return new Promise((resolve) => {
    const img = rootElement.querySelector<HTMLImageElement>('img[fetchpriority="high"]')
      || rootElement.querySelector<HTMLImageElement>('img');
    if (!img || document.hidden) {
      resolve();
      return;
    }
    const timer = setTimeout(() => resolve(), HERO_PAINT_TIMEOUT_MS);
    const frameAfterDecode = (): void => {
      clearTimeout(timer);
      if (document.hidden) {
        resolve();
        return;
      }
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    };
    img.decode().then(frameAfterDecode, frameAfterDecode);
  });
}

export type InitialConfig = {
  locale: string;
  config: string;
  everydoggyV5: boolean;
  tariff: string;
  pcfg: string;
  isPrerendered: boolean;
  hydrate?: boolean;
};

export function readInitialConfig(): InitialConfig | null {
  try {
    const el = document.getElementById('__INITIAL_CONFIG__');
    if (!el || !el.textContent) return null;
    return JSON.parse(el.textContent) as InitialConfig;
  } catch (e) {
    return null;
  }
}

// Hydration entry. Returns true when hydration was scheduled — the caller must
// NOT mount normally. Returns false for a normal load OR any fallback case
// (markup already wiped where needed).
export function setupPrerenderHydration(
  rootElement: HTMLElement,
  hydrateApp: () => void,
  mountApp: () => void,
): boolean {
  const cfg = readInitialConfig();
  if (!cfg || !cfg.isPrerendered) return false;
  // From here on the served file IS a pre-rendered one — every early return
  // below is a fallback to the classic SPA boot. page_load reads this flag.
  window.prerenderBoot = 'fallback';
  if (!cfg.hydrate) return false;
  if (!rootElement.firstElementChild) return false; // no static markup → normal boot

  // The promoted file is variant-locked: hydrate only when the visitor's resolved
  // config matches the baked one; anyone else gets the classic SPA boot with the
  // correct screen (graceful fallback).
  if (useAppStore.getState().config !== cfg.config) {
    // eslint-disable-next-line no-console
    console.warn('[prerender] config mismatch (url:', useAppStore.getState().config, 'baked:', cfg.config, ') — SPA boot');
    rootElement.innerHTML = '';
    return false;
  }

  let firstStep: string;
  try {
    firstStep = setupSurvey();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[prerender] setupSurvey failed — SPA boot', e);
    rootElement.innerHTML = '';
    return false;
  }

  // Only this pre-rendered page is affected; the normal SPA never hydrates.
  syncServerSnapshot(useAppStore as unknown as WithInitialState);
  syncServerSnapshot(useQuestionsStore as unknown as WithInitialState);

  preloadStepComponent(firstStep)
    .then(() => waitHeroPaint(rootElement))
    .then(() => {
      window.prerenderBoot = 'hydrate';
      hydrateApp();
    })
    .catch((e) => {
      // First-screen chunk failed — fall back to the classic SPA boot so the
      // user is never stuck on a dead static screen.
      // eslint-disable-next-line no-console
      console.warn('[prerender] first-screen chunk failed — SPA boot', e);
      window.prerenderBoot = 'fallback';
      rootElement.innerHTML = '';
      mountApp();
    });
  return true;
}
